import DOMPurify from "dompurify";
import { IsNullOrEmptyHtml } from "helpers/stringHelper";
import moment from "moment";
import type { ReactNode } from "react";

import type { UserCompanyProfile } from "newui/features/Auth";
import type { Rfx } from "newui/features/Rfx/types/rfx.types";

import type { BaseEmailFields } from "./EmailContent.interface";

export class RfxMail {
	rfx: Rfx;
	emailData: BaseEmailFields;
	additional_message?: string;
	sendToName?: string;
	currentCompany?: UserCompanyProfile;
	supplierCompanyName?: string;
	is_guest: boolean;

	constructor(
		rfx: Rfx,
		currentCompany: UserCompanyProfile,
		supplierCompanyName: string,
		additional_message = "",
		is_guest = false
	) {
		this.rfx = rfx;
		this.currentCompany = currentCompany;
		this.supplierCompanyName = supplierCompanyName;
		this.additional_message = DOMPurify.sanitize(additional_message);
		this.is_guest = is_guest;
		this.refreshEmailData();
	}

	public isAdditionalMessageEmpty(): boolean {
		return IsNullOrEmptyHtml(this.additional_message);
	}

	public deadline_date_time(includeDay = false): string {
		return `${includeDay ? this.emailData.deadline_day : ""} ${this.emailData.deadline_date} at ${this.emailData.deadline_time} ${this.emailData.deadline_time_zone}`;
	}

	public rfx_type_name_project_name(splitter = ","): ReactNode {
		return <>{this.rfx_name_project_name(splitter)}</>;
	}

	public rfx_name_project_name(splitter = ","): ReactNode {
		return (
			<>
				<b>
					{this.emailData.rfx_title}
					{splitter} {this.emailData.project_name}
				</b>
			</>
		);
	}

	public link_to_rfx(): ReactNode {
		return (
			<span style={{ color: "#003CE9", textDecoration: "underline" }}>
				{this.emailData.rfx_code}{" "}
				{this.emailData.rfx_title ?? "View on Procuracon Platform"}
			</span>
		);
	}

	public refreshEmailData() {
		this.emailData = this.generateEmailData();
	}

	public updateDateTime(
		newDate?: Date,
		newTime?: string,
		newTimeZone?: string
	) {
		let time = moment(this.rfx?.submissionDeadline).format("H:mm");
		let date = new Date(this.rfx?.submissionDeadline);
		let timeZone = this.emailData?.deadline_time_zone;

		if (newTime) {
			time = newTime;
		}

		if (newDate) {
			date = newDate;
		}

		if (newTimeZone) {
			timeZone = newTimeZone;
		}

		if (!date || !time || !timeZone) {
			return;
		}

		const [hours, minutes] = time.split(":").map(Number);

		const newSubmissionDeadline = moment(date)
			.hour(hours)
			.minute(minutes)
			.second(0)
			.millisecond(0);

		this.setSubmissionDateTime(this.emailData, newSubmissionDeadline, timeZone);
	}

	private setSubmissionDateTime(
		emailData: BaseEmailFields,
		submissionDeadline: moment.Moment,
		timeZone: string
	): void {
		emailData.deadline_date = submissionDeadline.format("D MMMM YYYY");
		emailData.deadline_day = submissionDeadline.format("dddd");
		emailData.deadline_time = submissionDeadline.format("h:mm A");
		emailData.deadline_24_time = submissionDeadline.format("H:mm");
		emailData.deadline_time_zone = timeZone;
	}

	private generateEmailData(): BaseEmailFields {
		const owner_company_name = this.currentCompany
			? this.currentCompany.name
			: "[Owner Company Name]";

		this.emailData = {
			project_name: this.rfx.project?.name || "[Project Name]",
			additional_message: this.additional_message,
			supplier_contact_name: "[Supplier Contact Name]",
			supplier_name: this.supplierCompanyName || "[Supplier Company Name]",
			rfx_url: `/rfx/${this.rfx.uuid}`,
			rfx_title: this.rfx.name || "[RFx Title]",
			rfx_type: this.getRfxType(),
			rfx_code: this.rfx.type || "RFx",
			owner_company_name: owner_company_name,
			contact: {
				contact_name:
					this.rfx.rfxLead?.fullName ||
					this.currentCompany?.contact_name ||
					"[Contact Name]",
				contact_phone_number:
					this.rfx.rfxLead?.phoneNumber ||
					this.currentCompany?.phone ||
					"[Contact Phone Number]",
				contact_email:
					this.rfx.rfxLead?.email ||
					this.currentCompany?.email ||
					"[Contact Email]",
				contact_position:
					this.rfx.rfxLead?.position ||
					this.rfx.type +
					" Lead for " +
					(owner_company_name || "[Owner Company Name]"),
			},
		};

		this.setSubmissionDateTime(
			this.emailData,
			moment(this.rfx.submissionDeadline),
			this.rfx.submissionTimezone || "[Time Zone]"
		);

		return this.emailData;
	}

	getRfxType = () => {
		if (!this.rfx.type) {
			return "RFx";
		}

		switch (this.rfx.type.toUpperCase()) {
			case "EOI":
				return "Expression of Interest";
			case "RFQ":
				return "Request for Quotation";
			case "RFP":
				return "Request for Proposal";
			case "RFI":
				return "Request for Information";
			default:
				return "RFx";
		}
	};
}
